/**
 * Role-based predicates and filters. Pure functions extracted from
 * users.service.js and usersRoles.service.js so the user domain
 * lives in a single place.
 *
 * Inputs accept a loose shape because callers pass either the API DTO
 * (with `role_guid`) or the form (with `role`). The predicates handle
 * both for backwards compatibility with the original service.
 */

import { FO, USER_ROLES } from './user.constants';

/** Loose user-like input accepted by the predicates. */
interface UserRoleLike {
  role?: string;
  role_guid?: string;
  guid?: string;
}

export function isTeacher(user?: UserRoleLike): boolean {
  return user?.role_guid === USER_ROLES.teacher;
}

export function isStudent(user?: UserRoleLike): boolean {
  return user?.role_guid === USER_ROLES.student;
}

export function isAdminEditorial(user?: UserRoleLike): boolean {
  return user?.role_guid === USER_ROLES.admin || user?.role === USER_ROLES.admin;
}

export function isHelpDesk(user?: UserRoleLike): boolean {
  return user?.role_guid === USER_ROLES.helpDesk || user?.role === USER_ROLES.helpDesk;
}

export function isPublisher(user?: UserRoleLike): boolean {
  return user?.role_guid === USER_ROLES.publisher || user?.role === USER_ROLES.publisher;
}

export function isUserBackOffice(user?: UserRoleLike): boolean {
  return isAdminEditorial(user) || isHelpDesk(user) || isPublisher(user);
}

export function canSeeFrontOffice(user?: UserRoleLike): boolean {
  const role = user?.role || user?.role_guid;
  return role !== undefined && [USER_ROLES.student as string, USER_ROLES.teacher as string].includes(role);
}

/**
 * Filter the back-office users out of a list. Mirrors the original
 * implementation which compared each user's `guid` against the FO
 * role codes — preserved verbatim even though semantically odd, so
 * existing call sites keep their behaviour.
 */
export function getUsersBO<T extends { guid?: string }>(users?: T[] | null): T[] {
  return users ? users.filter((user) => !FO.includes(user.guid as never)) : [];
}

/**
 * Filter the front-office users out of a list. See note on getUsersBO
 * regarding the `guid` vs role comparison.
 */
export function getUsersFO<T extends { guid?: string }>(users?: T[] | null): T[] {
  return users ? users.filter((user) => FO.includes(user.guid as never)) : [];
}
